import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Session, Component, TestCase, Feature, ApiLink, Platform, BugUrl, PlatformItem, TestPlan, PRFeedback } from '../../types';
import { apiRequest } from '../../utils/api';
import { formatDate } from '../../utils/date';
import { toggleTestedPlatform } from '../../utils/sessionHandlers';
import { useToast, checkPendingMessage, showTempMessage } from '../../utils/messages';
import { Toast } from '../../utils/messages';
import AddBugModal from './AddBugModal';
import AddComponentModal from './AddComponentModal';
import AddTestCaseModal from '../features/AddTestCaseModal';
import TestCaseReviewModal from '../features/TestCaseReviewModal';
import TestPlanDetails from '../testplan/TestPlanDetails';
import PRFeedbackModal from './PRFeedbackModal';
import ComponentDetailModal from './ComponentDetailModal';
// Theme is applied globally via CSS

const SessionRunner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  
  // Extract project code from session ID (e.g., "FS-42" → "FS")
  const projectCode = id?.match(/^([A-Z]+)-\d+$/)?.[1];
  const [components, setComponents] = useState<Component[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [allFeatures, setAllFeatures] = useState<Feature[]>([]); // All features for bug selector
  const [allComponents, setAllComponents] = useState<Component[]>([]); // All components for bug selector
  const [allPlatforms, setAllPlatforms] = useState<PlatformItem[]>([]); // All platforms for component selector
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [showAddBugModal, setShowAddBugModal] = useState(false);
  const [addBugModalData, setAddBugModalData] = useState<{
    featureId?: string;
    componentId?: string;
    platforms?: Platform[];
    testedPlatforms?: Platform[];
  } | null>(null);
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [bugLinks, setBugLinks] = useState<ApiLink[]>([]);
  const [showCreateTestCaseModal, setShowCreateTestCaseModal] = useState(false);
  const [selectedComponentForTestCase, setSelectedComponentForTestCase] = useState<string | null>(null);
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null);
  const [viewingTestCase, setViewingTestCase] = useState<TestCase | null>(null);
  const [selectedTestPlan, setSelectedTestPlan] = useState<TestPlan | null>(null);
  const [testPlanFeatures, setTestPlanFeatures] = useState<Feature[]>([]);
  const [testPlanComponents, setTestPlanComponents] = useState<Component[]>([]);
  const [testPlanName, setTestPlanName] = useState<string | null>(null);
  const [milestones, setMilestones] = useState<Record<string, { startDate: string; endDate?: string }>>({});
  const [showPRFeedbackModal, setShowPRFeedbackModal] = useState(false);
  const [prFeedbackModalData, setPrFeedbackModalData] = useState<{
    featureId?: string;
    componentId?: string;
  } | null>(null);
  const [editingPRFeedback, setEditingPRFeedback] = useState<PRFeedback | null>(null);
  const isRemovingPRFeedbackRef = useRef(false);
  const [viewingComponent, setViewingComponent] = useState<Component | null>(null);
  const [componentFilterStatus, setComponentFilterStatus] = useState<'tested' | 'untested' | null>(null);
  const [componentFilterPlatforms, setComponentFilterPlatforms] = useState<Platform[]>([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [expandedFilterSection, setExpandedFilterSection] = useState<'status' | 'platforms' | null>(null);
  const { message, setMessage, showMessage } = useToast();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showFilterDropdown && !target.closest('[data-filter-dropdown]') && !target.closest('[data-filter-button]')) {
        setShowFilterDropdown(false);
      }
    };
    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);
  
  // Check for pending messages from navigation
  useEffect(() => {
    checkPendingMessage(setMessage);
  }, [setMessage]);
  
  // Use refs to track values for cleanup without causing re-renders
  const isRunningRef = useRef(false);
  const timerRef = useRef(0);
  const sessionRef = useRef<Session | null>(null);
  const notesRef = useRef('');
  const isRemovingBugUrlRef = useRef(false);
  const isAddingBugUrlRef = useRef(false);

  // Fetch bug links
  useEffect(() => {
    const loadLinks = async () => {
      try {
        const bugLinksData = await apiRequest<ApiLink[]>('/api/apilinks?linkType=bug', {}, { useAuth: true }).catch(() => []);
        
        // Filter links by session's product
        // Show link if it has no productIds (applies to all) or includes the session's product
        const filterByProduct = (links: ApiLink[]) => {
          const productId = session?.productId;
          if (!productId) return links;
          return links.filter(link => 
            !link.productIds || link.productIds.length === 0 || link.productIds.includes(productId)
          );
        };
        
        setBugLinks(filterByProduct(bugLinksData || []));
      } catch (error) {
        console.error('Error fetching links:', error);
      }
    };
    
    loadLinks();
  }, [session?.productId]);
  
  // Fetch session when id changes
  useEffect(() => {
    const loadData = async () => {

      // Fetch session
      if (id) {
        try {
          const data = await apiRequest<Session>(`/api/sessions/${id}`, {}, { useAuth: true });
          setSession(data);
          
          // Fetch milestones if session has productId
          if (data.productId) {
            try {
              const apiUrl = `/api/milestones?productId=${encodeURIComponent(data.productId)}`;
              const milestonesData = await apiRequest<Array<{ name: string; startDate: string; endDate?: string }>>(
                apiUrl,
                {},
                { useAuth: true }
              );
              const milestoneMap: Record<string, { startDate: string; endDate?: string }> = {};
              (Array.isArray(milestonesData) ? milestonesData : []).forEach(m => {
                milestoneMap[m.name] = { startDate: m.startDate, endDate: m.endDate };
              });
              setMilestones(milestoneMap);
            } catch (error) {
              console.error('Error fetching milestones:', error);
              setMilestones({});
            }
          }
          
          // Fetch test plan name if testPlanUrl exists
          if (data.testPlanUrl) {
            const testPlanIdMatch = data.testPlanUrl.match(/\/testplan\/([^/?]+)/);
            const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
            if (testPlanId) {
              try {
                const testPlanData = await apiRequest<TestPlan>(`/api/testplans/${testPlanId}`, {}, { useAuth: true });
                setTestPlanName(testPlanData.title);
              } catch (error) {
                console.error('Error fetching test plan name:', error);
                setTestPlanName(null);
              }
            }
          }
          
          // Restore timer state
          // If startedAt exists, timer was running - calculate elapsed time and pause it in database
          // If startedAt doesn't exist, timer was paused - use saved duration
          if (data.status === 'inprogress' && data.startedAt) {
            // Timer was running - calculate elapsed time from startedAt to now
            const startedAt = new Date(data.startedAt);
            const now = new Date();
            const elapsedSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
            const savedDuration = data.duration || 0;
            // Use the maximum of saved duration and elapsed time
            // This handles cases where the timer was running when page was refreshed
            const currentDuration = Math.max(savedDuration, elapsedSeconds);
            setTimer(currentDuration);
            setIsRunning(false); // Always start paused after refresh
            
            // CRITICAL: Pause the timer in the database by saving the calculated duration
            // and clearing startedAt. This prevents the timer from continuing to run on subsequent refreshes.
            apiRequest(`/api/sessions/${data.id}`, {
              method: 'PUT',
              body: JSON.stringify({
                duration: currentDuration,
                startedAt: null // Clear startedAt to pause the timer
              })
            }, { useAuth: true }).catch(error => {
              console.error('Error pausing timer on refresh:', error);
            });
          } else if (data.duration !== undefined) {
            // Timer was paused - use saved duration
            setTimer(data.duration);
            setIsRunning(false);
          } else {
            setTimer(0);
            setIsRunning(false);
          }
          
          // Restore notes if they exist
          if (data.notes) {
            setNotes(data.notes);
          }
        } catch (error) {
          console.error('Error fetching session:', error);
        }
      }
    };
    loadData();
  }, [id]);

  // Fetch features, components, and test cases when session changes
  useEffect(() => {
    if (!session) return;

    const fetchSessionData = async () => {
      try {
        // Fetch all features for the product (for bug selector)
        if (session.productId) {
          const allFeaturesData = await apiRequest<Feature[]>(`/api/features?productId=${session.productId}`, {}, { useAuth: true });
          setAllFeatures(allFeaturesData || []);
        }

        // Fetch all components (for bug selector)
        const allComponentsData = await apiRequest<Component[]>('/api/components', {}, { useAuth: true });
        setAllComponents(allComponentsData || []);
        
        // Fetch all platforms
        const platformsData = await apiRequest<PlatformItem[]>('/api/platforms', {}, { useAuth: true });
        setAllPlatforms(platformsData || []);
        
        // Filter to session components for display (support both components and legacy subDependencies)
        // Check if components array exists and has items, otherwise fall back to subDependencies
        const sessionComponentIds = (session.components && session.components.length > 0) 
          ? session.components 
          : (session.subDependencies && session.subDependencies.length > 0 ? session.subDependencies : []);
        
        if (sessionComponentIds && sessionComponentIds.length > 0) {
          const sessionComponents = allComponentsData.filter((c: Component) => 
            sessionComponentIds.includes(c.id)
          );
          setComponents(sessionComponents);
          
          // Fetch test cases for these components
          const allTestCasesData = await apiRequest<TestCase[]>('/api/testcases', {}, { useAuth: true });
          const componentIds = new Set(sessionComponents.map(c => c.id));
          const sessionTestCases = allTestCasesData.filter((tc: TestCase) => 
            tc.componentId && componentIds.has(tc.componentId)
          );
          setTestCases(sessionTestCases);
        } else {
          setComponents([]);
          setTestCases([]);
        }
      } catch (error) {
        console.error('Error fetching session data:', error);
      }
    };
    fetchSessionData();
  }, [session]);

  // Update refs when state changes
  useEffect(() => {
    isRunningRef.current = isRunning;
    timerRef.current = timer;
    sessionRef.current = session;
    notesRef.current = notes;
  }, [isRunning, timer, session, notes]);

  // Timer interval - updates every second when running
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning]);

  // Page Visibility API - recalculate timer when tab becomes visible
  // This ensures accuracy even if setInterval was throttled in background tabs
  // Timer continues running in background - we just recalculate when visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      const currentSession = sessionRef.current;
      if (!currentSession) return;

      // When tab becomes visible, recalculate timer if it was running
      // This accounts for any throttling that may have occurred in background
      if (!document.hidden && isRunningRef.current && currentSession?.startedAt) {
          const startedAt = new Date(currentSession.startedAt);
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
          const savedDuration = currentSession.duration || 0;
          // Use the maximum of saved duration and elapsed time
          // This handles cases where the timer was running in background
          const currentDuration = Math.max(savedDuration, elapsedSeconds);
          setTimer(currentDuration);
        timerRef.current = currentDuration;
          
          // Update the database with the recalculated duration
          // Keep startedAt so we can continue recalculating if needed
          apiRequest(`/api/sessions/${currentSession.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              duration: currentDuration,
              startedAt: currentSession.startedAt // Keep startedAt since timer is still running
            })
          }, { useAuth: true })
          .then(updatedSession => {
            // Update local session state so periodic save uses latest data
            sessionRef.current = updatedSession;
            setSession(updatedSession);
          })
          .catch(error => {
            console.error('Error updating timer on visibility change:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRunning, session]);

  // Periodic save - save timer state every 30 seconds when running
  // This ensures we don't lose too much time if something goes wrong
  useEffect(() => {
    let saveInterval: NodeJS.Timeout | null = null;
    if (isRunning) {
      saveInterval = setInterval(() => {
        // Use refs to get latest values without causing re-renders
        const currentSession = sessionRef.current;
        if (currentSession?.startedAt) {
          const startedAt = new Date(currentSession.startedAt);
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
          const savedDuration = currentSession.duration || 0;
          const currentDuration = Math.max(savedDuration, elapsedSeconds);
          
          // Update local timer state
          setTimer(currentDuration);
          
          // Save to database (silently, don't show errors to user)
          apiRequest(`/api/sessions/${currentSession.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              duration: currentDuration,
              startedAt: currentSession.startedAt // Keep startedAt since timer is still running
            })
          }, { useAuth: true })
          .then(updatedSession => {
            // Update session ref so visibility handler has latest data
            sessionRef.current = updatedSession;
            setSession(updatedSession);
          })
          .catch(error => {
            console.error('Error periodically saving timer:', error);
          });
        }
      }, 30000); // Save every 30 seconds
    }
    return () => {
      if (saveInterval) clearInterval(saveInterval);
    };
  }, [isRunning]);

  // Stop timer when component unmounts (user navigates away)
  // This effect should only run on mount/unmount, not on state changes
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Save duration and all session data when page is being unloaded
      const currentSession = sessionRef.current;
      const currentTimer = timerRef.current;
      const timerWasRunning = isRunningRef.current;
      if (currentSession && currentSession.status === 'inprogress') {
        // Use sendBeacon for reliability during page unload
        const sessionData: any = {
          duration: currentTimer,
          status: 'inprogress',
          componentStatuses: currentSession.componentStatuses,
          subDepStatuses: currentSession.subDepStatuses, // Legacy
          bugUrls: currentSession.bugUrls,
          notes: notesRef.current || currentSession.notes
        };
        // Always pause the timer on page unload
        // Calculate final duration if timer was running
        let finalDuration = currentTimer;
        if (timerWasRunning && currentSession.startedAt) {
          const startedAt = new Date(currentSession.startedAt);
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
          finalDuration = Math.max(currentTimer, elapsedSeconds);
        }
        sessionData.duration = finalDuration;
        sessionData.startedAt = null; // Always clear startedAt to pause timer
        const blob = new Blob([JSON.stringify(sessionData)], { type: 'application/json' });
        navigator.sendBeacon(`/api/sessions/${currentSession.id}`, blob);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Cleanup: save session state when leaving (navigating to another tab)
      // Use refs to get current values without dependencies
      const currentSession = sessionRef.current;
      const currentTimer = timerRef.current;
      
      // CRITICAL: Don't save if the session was just completed
      // The session status might have been updated to 'completed' but the ref hasn't updated yet
      // Check if we're navigating away after a submit by checking if the session is being completed
      // We'll skip the cleanup save if the session status is 'completed' or if we just submitted
      if (currentSession && currentSession.status === 'inprogress') {
        // Always pause the timer when unmounting (user navigates away)
        // Calculate final duration if timer was running
        let finalDuration = currentTimer;
        const timerWasRunning = isRunningRef.current;
        if (timerWasRunning && currentSession.startedAt) {
          const startedAt = new Date(currentSession.startedAt);
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
          finalDuration = Math.max(currentTimer, elapsedSeconds);
        }
        
        const sessionData: any = {
          duration: finalDuration,
          startedAt: null, // Always clear startedAt to pause timer when navigating away
          status: 'inprogress',
          subDepStatuses: currentSession.subDepStatuses,
          bugUrls: currentSession.bugUrls,
          notes: notesRef.current || currentSession.notes
        };
        
        // Try regular apiRequest first (more reliable for navigation within app)
        apiRequest(`/api/sessions/${currentSession.id}`, {
          method: 'PUT',
          body: JSON.stringify(sessionData),
          keepalive: true // Keep request alive even if page is unloading
        }, { useAuth: true }).catch(err => {
          console.error('Error saving session on unmount:', err);
          // Fallback to sendBeacon if apiRequest fails
          const blob = new Blob([JSON.stringify(sessionData)], { type: 'application/json' });
          navigator.sendBeacon(`/api/sessions/${currentSession.id}`, blob);
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount/unmount

  const fetchSession = async (preserveTimer: boolean = false): Promise<Session | null> => {
    try {
      const data = await apiRequest<Session>(`/api/sessions/${id}`, {}, { useAuth: true });
      setSession(data);
      
      // Fetch test plan name if testPlanUrl exists
      if (data.testPlanUrl) {
        const testPlanIdMatch = data.testPlanUrl.match(/\/testplan\/([^/?]+)/);
        const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
        if (testPlanId) {
          try {
            const testPlanData = await apiRequest<TestPlan>(`/api/testplans/${testPlanId}`, {}, { useAuth: true });
            setTestPlanName(testPlanData.title);
          } catch (error) {
            console.error('Error fetching test plan name:', error);
            setTestPlanName(null);
          }
        }
      }
      
      // Only update timer if we're not preserving it (e.g., on initial load)
      if (!preserveTimer) {
        // Restore timer state
        // If startedAt exists, timer was running - calculate elapsed time and pause it in database
        // If startedAt doesn't exist, timer was paused - use saved duration
        if (data.status === 'inprogress' && data.startedAt) {
          // Timer was running - calculate elapsed time from startedAt to now
          const startedAt = new Date(data.startedAt);
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
          const savedDuration = data.duration || 0;
          // Use the maximum of saved duration and elapsed time
          // This handles cases where the timer was running when page was refreshed
          const currentDuration = Math.max(savedDuration, elapsedSeconds);
          setTimer(currentDuration);
          setIsRunning(false); // Always start paused after refresh
          
          // CRITICAL: Pause the timer in the database by saving the calculated duration
          // and clearing startedAt. This prevents the timer from continuing to run on subsequent refreshes.
          apiRequest(`/api/sessions/${data.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              duration: currentDuration,
              startedAt: null // Clear startedAt to pause the timer
            })
          }, { useAuth: true }).catch(error => {
            console.error('Error pausing timer on refresh:', error);
          });
        } else if (data.duration !== undefined) {
          // Timer was paused - use saved duration
          setTimer(data.duration);
          setIsRunning(false); // Always start paused - don't auto-start timer
        } else {
          setTimer(0);
          setIsRunning(false);
        }
      }
      // If preserving timer, keep the current timer value (don't reset it)
      
      // Restore notes if they exist
      if (data.notes) {
        setNotes(data.notes);
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching session:', error);
      return null;
    }
  };

  const handlePlatformTestedToggle = async (componentId: string, platform: Platform) => {
    if (!session) return;

    const updatedSession = toggleTestedPlatform(session, componentId, platform);
    setSession(updatedSession);
    
    // Auto-save progress
    try {
      await apiRequest(`/api/sessions/${session.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          componentStatuses: updatedSession.componentStatuses
        })
      }, { useAuth: true });
    } catch (error) {
      console.error('Error auto-saving session:', error);
    }
  };

  const handleMarkAllAsTested = async () => {
    if (!session || !components.length) return;

    const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : []);
    if (sessionPlatforms.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'No platforms selected for this session' });
      return;
    }

    const componentStatuses = [...(session.componentStatuses || [])];
    let hasChanges = false;
    let markedCount = 0;
    const now = new Date().toISOString();

    // For each component, mark all untested platforms as tested
    components.forEach(component => {
      const statusIndex = componentStatuses.findIndex(s => s.componentId === component.id);
      const existingStatus = statusIndex >= 0 ? componentStatuses[statusIndex] : null;
      const testedPlatforms = existingStatus?.testedPlatforms || [];
      const testedAt = existingStatus?.testedAt || {};
      
      // Find untested platforms for this component
      const untestedPlatforms = sessionPlatforms.filter(platform => !testedPlatforms.includes(platform));
      
      if (untestedPlatforms.length > 0) {
        hasChanges = true;
        markedCount++; // Count components that had untested platforms
        const allTestedPlatforms = Array.from(new Set([...testedPlatforms, ...untestedPlatforms]));
        
        // Add testedAt timestamps for newly tested platforms
        const newTestedAt: Partial<Record<Platform, string>> = { ...testedAt };
        untestedPlatforms.forEach(platform => {
          if (!newTestedAt[platform]) {
            newTestedAt[platform] = now;
          }
        });
        
        if (statusIndex >= 0) {
          componentStatuses[statusIndex] = {
            ...componentStatuses[statusIndex],
            tested: true,
            testedPlatforms: allTestedPlatforms,
            testedAt: newTestedAt
          };
        } else {
          componentStatuses.push({
            componentId: component.id,
            tested: true,
            testedPlatforms: allTestedPlatforms,
            testedAt: newTestedAt,
            bugged: false
          });
        }
      }
    });

    if (!hasChanges) {
      showTempMessage(setMessage, { type: 'success', text: 'All components are already marked as tested' });
      return;
    }

    const updatedSession = {
      ...session,
      componentStatuses
    };
    
    setSession(updatedSession);
    
    // Save to server
    try {
      await apiRequest(`/api/sessions/${session.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          componentStatuses
        })
      }, { useAuth: true });
      
      showTempMessage(setMessage, { 
        type: 'success', 
        text: `Marked all untested components as tested (${markedCount} component${markedCount !== 1 ? 's' : ''})` 
      });
    } catch (error) {
      console.error('Error marking all as tested:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to save changes. Please try again.' });
    }
  };

  const handleBugAdded = async (newBug: BugUrl) => {
    // Prevent concurrent bug additions
    if (isAddingBugUrlRef.current) {
      console.warn('Bug addition already in progress, skipping duplicate request');
      return;
    }
    isAddingBugUrlRef.current = true;

    try {
      if (!session) {
        showMessage({ type: 'error', text: 'Session not loaded. Please refresh the page.' });
        isAddingBugUrlRef.current = false;
        return;
      }

      // Validate required fields
      const componentId = newBug.componentId;
      if (!componentId) {
        showMessage({ type: 'error', text: 'Component is required for bug tracking.' });
        isAddingBugUrlRef.current = false;
        return;
      }

      if (!newBug.platforms || newBug.platforms.length === 0) {
        showMessage({ type: 'error', text: 'At least one platform is required for bug tracking.' });
        isAddingBugUrlRef.current = false;
        return;
      }

      // Check for duplicates - same URL on same component is not allowed
      const existingBugUrls = session.bugUrls || [];
      const isDuplicate = existingBugUrls.some((bugUrl: BugUrl) => {
        return bugUrl.url.toLowerCase() === newBug.url.toLowerCase() && 
               bugUrl.componentId === componentId;
      });

      if (isDuplicate) {
        showMessage({ type: 'error', text: 'This bug URL already exists for this component. Duplicates are not allowed on the same component.' });
        isAddingBugUrlRef.current = false;
        return;
      }

      // Build updated session with new bug
      const updatedBugUrls: BugUrl[] = [...existingBugUrls, newBug];
      const platformsToMark = newBug.platforms;
      const now = new Date().toISOString();

      // Update component statuses - mark component as tested and bugged
      const componentStatuses = [...(session.componentStatuses || [])];
      const statusIndex = componentStatuses.findIndex(s => s.componentId === componentId);
      
      if (statusIndex >= 0) {
        const existing = componentStatuses[statusIndex];
        // Add testedAt timestamps for newly tested platforms
        const testedAt = { ...(existing.testedAt || {}) };
        platformsToMark.forEach(platform => {
          if (!testedAt[platform]) {
            testedAt[platform] = now;
          }
        });
        componentStatuses[statusIndex] = {
          ...existing,
          tested: true,
          testedPlatforms: Array.from(new Set([...(existing.testedPlatforms || []), ...platformsToMark])),
          testedAt: Object.keys(testedAt).length > 0 ? testedAt : undefined,
          bugged: true,
          buggedPlatforms: Array.from(new Set([...(existing.buggedPlatforms || []), ...platformsToMark]))
        };
      } else {
        // Create testedAt for all platforms being marked
        const testedAt: Partial<Record<Platform, string>> = {};
        platformsToMark.forEach(platform => {
          testedAt[platform] = now;
        });
        componentStatuses.push({
          componentId,
          tested: true,
          testedPlatforms: platformsToMark,
          testedAt,
          bugged: true,
          buggedPlatforms: platformsToMark
        });
      }

      // Ensure component and feature are in session
      const sessionComponentIds = session.components || [];
      const updatedComponents = sessionComponentIds.includes(componentId) 
        ? sessionComponentIds 
        : [...sessionComponentIds, componentId];

      const features = session.features || [];
      const updatedFeatures = newBug.featureId && !features.includes(newBug.featureId)
        ? [...features, newBug.featureId]
        : features;

      // Optimistic update
      const optimisticSession: Session = {
        ...session,
        bugUrls: updatedBugUrls,
        componentStatuses,
        components: updatedComponents,
        features: updatedFeatures
      };

      setSession(optimisticSession);
      sessionRef.current = optimisticSession;

      // Update components list if needed (for UI display)
      if (!sessionComponentIds.includes(componentId)) {
        const newComponent = allComponents.find(c => c.id === componentId);
        if (newComponent) {
          const componentExists = components.some(comp => comp.id === componentId);
          if (!componentExists) {
            setComponents(prev => [...prev, newComponent]);
          }
        }
      }

      // Save to server - fetch latest session first to ensure we have the latest version
      // This prevents 409 conflicts when multiple users/tabs are updating the same session
      // IMPORTANT: Fetch without updating state to preserve our optimistic update
      let latestSession: Session | null = null;
      try {
        const data = await apiRequest<Session>(`/api/sessions/${session.id}`, {}, { useAuth: true });
        latestSession = data;
        // Update ref but NOT state - we want to keep our optimistic update visible
        sessionRef.current = data;
      } catch (error) {
        console.error('Error fetching latest session:', error);
        showMessage({ type: 'error', text: 'Failed to refresh session. Please try again.' });
        return;
      }
      
      if (!latestSession) {
        showMessage({ type: 'error', text: 'Failed to refresh session. Please try again.' });
        return;
      }

      // Check if bug was already added by another process
      const latestBugUrls = latestSession.bugUrls || [];
      const alreadyAdded = latestBugUrls.some((bug: BugUrl) => 
        bug.url.toLowerCase() === newBug.url.toLowerCase() && 
        bug.componentId === componentId
      );

      if (alreadyAdded) {
        // Bug was already added - verify component status is correct and update session state
        const latestComponentStatuses = [...(latestSession.componentStatuses || [])];
        const statusIdx = latestComponentStatuses.findIndex(s => s.componentId === componentId);
        
        if (statusIdx >= 0) {
          const existing = latestComponentStatuses[statusIdx];
          // Add testedAt timestamps for newly tested platforms
          const testedAt = { ...(existing.testedAt || {}) };
          platformsToMark.forEach(platform => {
            if (!testedAt[platform]) {
              testedAt[platform] = now;
            }
          });
          // Ensure component is marked as bugged with correct platforms
          latestComponentStatuses[statusIdx] = {
            ...existing,
            tested: true,
            testedPlatforms: Array.from(new Set([...(existing.testedPlatforms || []), ...platformsToMark])),
            testedAt: Object.keys(testedAt).length > 0 ? testedAt : undefined,
            bugged: true,
            buggedPlatforms: Array.from(new Set([...(existing.buggedPlatforms || []), ...platformsToMark]))
          };
        } else {
          // Component status missing - add it with testedAt
          const testedAt: Partial<Record<Platform, string>> = {};
          platformsToMark.forEach(platform => {
            testedAt[platform] = now;
          });
          latestComponentStatuses.push({
            componentId,
            tested: true,
            testedPlatforms: platformsToMark,
            testedAt,
            bugged: true,
            buggedPlatforms: platformsToMark
          });
        }

        const updatedSession = {
          ...latestSession,
          componentStatuses: latestComponentStatuses
        };
        setSession(updatedSession);
        sessionRef.current = updatedSession;
        isAddingBugUrlRef.current = false;
        return;
      }

      // Merge our new bug with the latest session state
      const mergedBugUrls = [...latestBugUrls, newBug];
      
      // Recalculate component statuses based on latest session + our new bug
      const latestComponentStatuses = [...(latestSession.componentStatuses || [])];
      const statusIdx = latestComponentStatuses.findIndex(s => s.componentId === componentId);
      
      if (statusIdx >= 0) {
        const existing = latestComponentStatuses[statusIdx];
        latestComponentStatuses[statusIdx] = {
          ...existing,
          tested: true,
          testedPlatforms: Array.from(new Set([...(existing.testedPlatforms || []), ...platformsToMark])),
          bugged: true,
          buggedPlatforms: Array.from(new Set([...(existing.buggedPlatforms || []), ...platformsToMark]))
        };
      } else {
        latestComponentStatuses.push({
          componentId,
          tested: true,
          testedPlatforms: platformsToMark,
          bugged: true,
          buggedPlatforms: platformsToMark
        });
      }

      // Ensure component and feature are in latest session
      const latestComponents = latestSession.components || [];
      const mergedComponents = latestComponents.includes(componentId) 
        ? latestComponents 
        : [...latestComponents, componentId];

      const latestFeatures = latestSession.features || [];
      const mergedFeatures = newBug.featureId && !latestFeatures.includes(newBug.featureId)
        ? [...latestFeatures, newBug.featureId]
        : latestFeatures;

      try {
        const savedSession = await apiRequest<Session>(`/api/sessions/${latestSession.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            bugUrls: mergedBugUrls,
            componentStatuses: latestComponentStatuses,
            components: mergedComponents,
            features: mergedFeatures,
            version: latestSession.version || 1,
            duration: timerRef.current,
            notes: notesRef.current || latestSession.notes || null
          })
        }, { useAuth: true });

        // Verify our bug is in the server response - if not, add it back
        // This handles cases where the server might have filtered it out or there was a merge issue
        const savedBugUrls = savedSession.bugUrls || [];
        const bugExists = savedBugUrls.some((bug: BugUrl) => 
          bug.url.toLowerCase() === newBug.url.toLowerCase() && 
          bug.componentId === componentId
        );

        let finalSession = savedSession;
        if (!bugExists) {
          // Bug is missing from server response - add it back
          console.warn('Bug missing from server response, adding it back', {
            url: newBug.url,
            componentId: componentId,
            serverBugCount: savedBugUrls.length
          });
          finalSession = {
            ...savedSession,
            bugUrls: [...savedBugUrls, newBug],
            componentStatuses: latestComponentStatuses // Use our updated component statuses
          };
        }

        // Use server response (with bug added back if needed) as source of truth
        setSession(finalSession);
        sessionRef.current = finalSession;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        // On conflict (should be rare since we fetched latest session), refresh and check
        if (errorMessage.includes('409') || errorMessage.includes('Conflict')) {
          const freshSession = await fetchSession(true);
          if (freshSession) {
            // Check if bug was already added
            const freshBugUrls = freshSession.bugUrls || [];
            const alreadyAdded = freshBugUrls.some((bug: BugUrl) => 
              bug.url.toLowerCase() === newBug.url.toLowerCase() && 
              bug.componentId === componentId
            );

            if (alreadyAdded) {
              // Bug was already added - success
              setSession(freshSession);
              sessionRef.current = freshSession;
              isAddingBugUrlRef.current = false;
              return;
            }

            // Still not added - retry once more with fresh session
            const retryBugUrls = [...freshBugUrls, newBug];
            const retryComponentStatuses = [...(freshSession.componentStatuses || [])];
            const retryStatusIdx = retryComponentStatuses.findIndex(s => s.componentId === componentId);
            
            if (retryStatusIdx >= 0) {
              const existing = retryComponentStatuses[retryStatusIdx];
              // Add testedAt timestamps for newly tested platforms
              const testedAt = { ...(existing.testedAt || {}) };
              platformsToMark.forEach(platform => {
                if (!testedAt[platform]) {
                  testedAt[platform] = now;
                }
              });
              retryComponentStatuses[retryStatusIdx] = {
                ...existing,
                tested: true,
                testedPlatforms: Array.from(new Set([...(existing.testedPlatforms || []), ...platformsToMark])),
                testedAt: Object.keys(testedAt).length > 0 ? testedAt : undefined,
                bugged: true,
                buggedPlatforms: Array.from(new Set([...(existing.buggedPlatforms || []), ...platformsToMark]))
              };
            } else {
              // Create testedAt for all platforms being marked
              const testedAt: Partial<Record<Platform, string>> = {};
              platformsToMark.forEach(platform => {
                testedAt[platform] = now;
              });
              retryComponentStatuses.push({
                componentId,
                tested: true,
                testedPlatforms: platformsToMark,
                testedAt,
                bugged: true,
                buggedPlatforms: platformsToMark
              });
            }

            const retryComponents = freshSession.components || [];
            const retryMergedComponents = retryComponents.includes(componentId) 
              ? retryComponents 
              : [...retryComponents, componentId];

            const retryFeatures = freshSession.features || [];
            const retryMergedFeatures = newBug.featureId && !retryFeatures.includes(newBug.featureId)
              ? [...retryFeatures, newBug.featureId]
              : retryFeatures;

            try {
              const retrySession = await apiRequest<Session>(`/api/sessions/${freshSession.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                  bugUrls: retryBugUrls,
                  componentStatuses: retryComponentStatuses,
                  components: retryMergedComponents,
                  features: retryMergedFeatures,
                  version: freshSession.version || 1,
                  duration: timerRef.current,
                  notes: notesRef.current || freshSession.notes || null
                })
              }, { useAuth: true });

              setSession(retrySession);
              sessionRef.current = retrySession;
              isAddingBugUrlRef.current = false;
              return;
            } catch (retryError) {
              // Retry also failed - show error but keep optimistic update
              showMessage({ 
                type: 'error', 
                text: 'Failed to save bug after retry. The bug has been added locally. Please refresh the page.' 
              });
            }
          } else {
            showMessage({ 
              type: 'error', 
              text: 'Failed to refresh session. The bug has been added locally. Please refresh the page.' 
            });
          }
        } else {
          // For other errors, keep optimistic update but show error
          showMessage({ 
            type: 'error', 
            text: `Failed to save bug: ${errorMessage}. The bug has been added locally.` 
          });
        }
      }
    } finally {
      isAddingBugUrlRef.current = false;
    }
  };

  const handlePRFeedbackAdded = async (newFeedback: PRFeedback, isEdit: boolean = false) => {
    try {
      if (!session) {
        showMessage({ type: 'error', text: 'Session not loaded. Please refresh the page.' });
        return;
      }

      // Validate required fields
      const componentId = newFeedback.componentId;
      if (!componentId) {
        showMessage({ type: 'error', text: 'Component is required for PR feedback.' });
        return;
      }

      if (!newFeedback.commentUrl || !newFeedback.commentUrl.trim()) {
        showMessage({ type: 'error', text: 'Comment URL is required for PR feedback.' });
        return;
      }

      // Check for duplicate comment URL on the same component (only for new feedback, not edits)
      if (!isEdit) {
        const existingFeedbacks = session.prFeedbacks || [];
        const trimmedCommentUrl = newFeedback.commentUrl.trim().toLowerCase();
        const duplicate = existingFeedbacks.find(fb => 
          fb.componentId === componentId && 
          fb.commentUrl && 
          fb.commentUrl.trim().toLowerCase() === trimmedCommentUrl
        );
        
        if (duplicate) {
          showMessage({ type: 'error', text: 'This comment URL has already been added to this component. Duplicate PR feedback is not allowed on the same component.' });
          return;
        }
      }

      // Build updated session with new or updated PR feedback
      const existingFeedbacks = session.prFeedbacks || [];
      let updatedFeedbacks: PRFeedback[];
      if (isEdit && editingPRFeedback) {
        // Update existing feedback
        updatedFeedbacks = existingFeedbacks.map(fb => 
          fb.id === editingPRFeedback.id ? newFeedback : fb
        );
      } else {
        // Add new feedback
        updatedFeedbacks = [...existingFeedbacks, newFeedback];
      }

      // Ensure component and feature are in session
      const sessionComponentIds = session.components || [];
      const updatedComponents = sessionComponentIds.includes(componentId) 
        ? sessionComponentIds 
        : [...sessionComponentIds, componentId];

      const features = session.features || [];
      const updatedFeatures = newFeedback.featureId && !features.includes(newFeedback.featureId)
        ? [...features, newFeedback.featureId]
        : features;

      // Optimistic update
      const optimisticSession: Session = {
        ...session,
        prFeedbacks: updatedFeedbacks,
        components: updatedComponents,
        features: updatedFeatures
      };

      setSession(optimisticSession);
      sessionRef.current = optimisticSession;

      // Update components list if needed (for UI display)
      if (!sessionComponentIds.includes(componentId)) {
        const newComponent = allComponents.find(c => c.id === componentId);
        if (newComponent) {
          const componentExists = components.some(comp => comp.id === componentId);
          if (!componentExists) {
            setComponents(prev => [...prev, newComponent]);
          }
        }
      }

      // Save to server - fetch latest session first to ensure we have the latest version
      let latestSession: Session | null = null;
      try {
        const data = await apiRequest<Session>(`/api/sessions/${session.id}`, {}, { useAuth: true });
        latestSession = data;
        sessionRef.current = data;
      } catch (error) {
        console.error('Error fetching latest session:', error);
        showMessage({ type: 'error', text: 'Failed to refresh session. Please try again.' });
        return;
      }
      
      if (!latestSession) {
        showMessage({ type: 'error', text: 'Failed to refresh session. Please try again.' });
        return;
      }

      // Merge our new or updated feedback with the latest session state
      const latestFeedbacks = latestSession.prFeedbacks || [];
      
      // Check for duplicate comment URL on the same component (only for new feedback, not edits)
      if (!isEdit) {
        const trimmedCommentUrl = newFeedback.commentUrl.trim().toLowerCase();
        const duplicate = latestFeedbacks.find(fb => 
          fb.componentId === componentId && 
          fb.commentUrl && 
          fb.commentUrl.trim().toLowerCase() === trimmedCommentUrl
        );
        
        if (duplicate) {
          showMessage({ type: 'error', text: 'This comment URL has already been added to this component. Duplicate PR feedback is not allowed on the same component.' });
          // Revert optimistic update
          setSession(latestSession);
          sessionRef.current = latestSession;
          return;
        }
      }
      
      let mergedFeedbacks: PRFeedback[];
      if (isEdit && editingPRFeedback) {
        // Update existing feedback
        mergedFeedbacks = latestFeedbacks.map(fb => 
          fb.id === editingPRFeedback.id ? newFeedback : fb
        );
      } else {
        // Add new feedback
        mergedFeedbacks = [...latestFeedbacks, newFeedback];
      }

      // Ensure component and feature are in latest session
      const latestComponents = latestSession.components || [];
      const mergedComponents = latestComponents.includes(componentId) 
        ? latestComponents 
        : [...latestComponents, componentId];

      const latestFeatures = latestSession.features || [];
      const mergedFeatures = newFeedback.featureId && !latestFeatures.includes(newFeedback.featureId)
        ? [...latestFeatures, newFeedback.featureId]
        : latestFeatures;

      try {
        const savedSession = await apiRequest<Session>(`/api/sessions/${latestSession.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            prFeedbacks: mergedFeedbacks,
            components: mergedComponents,
            features: mergedFeatures,
            version: latestSession.version || 1,
            duration: timerRef.current,
            notes: notesRef.current || latestSession.notes || null
          })
        }, { useAuth: true });

        // Use server response as source of truth
        setSession(savedSession);
        sessionRef.current = savedSession;
        setEditingPRFeedback(null);
        showMessage({ type: 'success', text: isEdit ? 'PR feedback updated successfully' : 'PR feedback added successfully' });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        // On conflict, refresh and retry
        if (errorMessage.includes('409') || errorMessage.includes('Conflict')) {
          const freshSession = await fetchSession(true);
          if (freshSession) {
            const freshFeedbacks = freshSession.prFeedbacks || [];
            let retryFeedbacks: PRFeedback[];
            if (isEdit && editingPRFeedback) {
              retryFeedbacks = freshFeedbacks.map(fb => 
                fb.id === editingPRFeedback.id ? newFeedback : fb
              );
            } else {
              retryFeedbacks = [...freshFeedbacks, newFeedback];
            }

            // Ensure component and feature are in fresh session
            const freshComponents = freshSession.components || [];
            const retryComponents = freshComponents.includes(componentId) 
              ? freshComponents 
              : [...freshComponents, componentId];

            const freshFeatures = freshSession.features || [];
            const retryFeatures = newFeedback.featureId && !freshFeatures.includes(newFeedback.featureId)
              ? [...freshFeatures, newFeedback.featureId]
              : freshFeatures;

            try {
              const retrySession = await apiRequest<Session>(`/api/sessions/${freshSession.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                  prFeedbacks: retryFeedbacks,
                  components: retryComponents,
                  features: retryFeatures,
                  version: freshSession.version || 1,
                  duration: timerRef.current,
                  notes: notesRef.current || freshSession.notes || null
                })
              }, { useAuth: true });

              setSession(retrySession);
              sessionRef.current = retrySession;
              setEditingPRFeedback(null);
              showMessage({ type: 'success', text: isEdit ? 'PR feedback updated successfully' : 'PR feedback added successfully' });
              return;
            } catch (retryError) {
              showMessage({ 
                type: 'error', 
                text: 'Failed to save PR feedback after retry. Please refresh the page.' 
              });
            }
          }
        } else {
          showMessage({ 
            type: 'error', 
            text: `Failed to save PR feedback: ${errorMessage}` 
          });
        }
      }
    } catch (error) {
      console.error('Error adding PR feedback:', error);
      showMessage({ type: 'error', text: isEdit ? 'Failed to update PR feedback. Please try again.' : 'Failed to add PR feedback. Please try again.' });
    }
  };

  const handleRemovePRFeedback = async (feedbackId: string) => {
    if (!session) return;
    if (isRemovingPRFeedbackRef.current) {
      return;
    }

    isRemovingPRFeedbackRef.current = true;

    try {
      // Get the feedback to remove from current session state
      const prFeedbacks = session.prFeedbacks || [];
      const feedbackToRemove = prFeedbacks.find(fb => fb.id === feedbackId);
      
      if (!feedbackToRemove) {
        // Already removed or invalid
        isRemovingPRFeedbackRef.current = false;
        return;
      }

      // Build updated feedbacks (remove the one we're deleting)
      const updatedFeedbacks = prFeedbacks.filter(fb => fb.id !== feedbackId);

      // Optimistic update
      const optimisticSession: Session = {
        ...session,
        prFeedbacks: updatedFeedbacks
      };

      setSession(optimisticSession);
      sessionRef.current = optimisticSession;

      // Save to server
      try {
        const savedSession = await apiRequest<Session>(`/api/sessions/${session.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            prFeedbacks: updatedFeedbacks,
            version: session.version || 1,
            duration: timerRef.current,
            notes: notesRef.current || session.notes || null
          })
        }, { useAuth: true });
        
        // Use server response as source of truth
        setSession(savedSession);
        sessionRef.current = savedSession;
        showMessage({ type: 'success', text: 'PR feedback removed successfully' });
      } catch (error) {
        console.error('Error removing PR feedback:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isConflict = errorMessage.includes('409') || 
                          errorMessage.includes('Conflict') ||
                          errorMessage.toLowerCase().includes('conflict');
        
        if (isConflict) {
          // On conflict, refresh and retry
          const freshSession = await fetchSession(true);
          if (freshSession) {
            const freshFeedbacks = freshSession.prFeedbacks || [];
            const stillExists = freshFeedbacks.some(fb => fb.id === feedbackId);

            if (!stillExists) {
              // Already removed - success
              setSession(freshSession);
              sessionRef.current = freshSession;
              isRemovingPRFeedbackRef.current = false;
              return;
            }

            // Retry with fresh session
            const retryFeedbacks = freshFeedbacks.filter(fb => fb.id !== feedbackId);

            const retrySession = await apiRequest<Session>(`/api/sessions/${freshSession.id}`, {
              method: 'PUT',
              body: JSON.stringify({
                prFeedbacks: retryFeedbacks,
                version: freshSession.version || 1,
                duration: timerRef.current,
                notes: notesRef.current || freshSession.notes || null
              })
            }, { useAuth: true });

            setSession(retrySession);
            sessionRef.current = retrySession;
            showMessage({ type: 'success', text: 'PR feedback removed successfully' });
            isRemovingPRFeedbackRef.current = false;
            return;
          }
        }
        
        // For other errors, keep optimistic update but show error
        showMessage({ 
          type: 'error', 
          text: `Failed to remove PR feedback: ${errorMessage}. The feedback has been removed locally.` 
        });
      }
    } finally {
      isRemovingPRFeedbackRef.current = false;
    }
  };

  const handleTestCaseAdded = async (newTestCase: TestCase) => {
    if (!session) return;

    // Add or update test case in local state
    setTestCases(prev => {
      const existingIndex = prev.findIndex(tc => tc.id === newTestCase.id);
      if (existingIndex !== -1) {
        // Update existing test case
        const updated = [...prev];
        updated[existingIndex] = newTestCase;
        return updated;
      }
      // Add new test case
      return [...prev, newTestCase];
    });

    // If the test case belongs to a component in this session, it will automatically appear
    // since we filter testCases by session.components
    showMessage({ type: 'success', text: 'Test case added successfully' });
  };

  const handleComponentsAdded = async (featureIds: string[], componentIds: string[]) => {
    if (!session) return;

    try {
      // Fetch latest session first to ensure we have the latest version
      // This prevents 409 conflicts when multiple users/tabs are updating the same session
      let latestSession: Session | null = null;
      try {
        const data = await apiRequest<Session>(`/api/sessions/${session.id}`, {}, { useAuth: true });
        latestSession = data;
        // Update ref but NOT state - we want to keep our optimistic update visible
        sessionRef.current = data;
      } catch (error) {
        console.error('Error fetching latest session:', error);
        showMessage({ type: 'error', text: 'Failed to refresh session. Please try again.' });
        return;
      }
      
      if (!latestSession) {
        showMessage({ type: 'error', text: 'Failed to refresh session. Please try again.' });
        return;
      }

      // Check which components are already in the session
      const latestComponents = latestSession.components || [];
      const componentsToAdd = componentIds.filter(id => !latestComponents.includes(id));
      
      if (componentsToAdd.length === 0) {
        // All components already added - just update session state
        setSession(latestSession);
        sessionRef.current = latestSession;
        showMessage({ type: 'success', text: 'All selected components are already in the session.' });
        return;
      }

      // Add to local components state
      const componentsToAddObjects = allComponents.filter(c => componentsToAdd.includes(c.id));
      setComponents(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const toAdd = componentsToAddObjects.filter(c => !existingIds.has(c.id));
        return [...prev, ...toAdd];
      });

      // Merge with latest session components
      const updatedComponents = [...latestComponents];
      componentsToAdd.forEach(componentId => {
        if (!updatedComponents.includes(componentId)) {
          updatedComponents.push(componentId);
        }
      });

      // Merge features from callback and ensure component features are included
      const latestFeatures = latestSession.features || [];
      const updatedFeatures = [...latestFeatures];
      
      // Add features from callback
      featureIds.forEach(featureId => {
        if (!updatedFeatures.includes(featureId)) {
          updatedFeatures.push(featureId);
        }
      });
      
      // Also ensure component features are included (as a fallback)
      componentsToAddObjects.forEach(component => {
        if (component.featureId && !updatedFeatures.includes(component.featureId)) {
          updatedFeatures.push(component.featureId);
        }
      });

      // Save to server
      const updatedSession = await apiRequest<Session>(`/api/sessions/${latestSession.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          components: updatedComponents,
          features: updatedFeatures,
          version: latestSession.version || 1,
          duration: timerRef.current,
          notes: notesRef.current || latestSession.notes || null
        })
      }, { useAuth: true });

      setSession(updatedSession);
      sessionRef.current = updatedSession;
      showMessage({ type: 'success', text: `${componentsToAdd.length} component${componentsToAdd.length !== 1 ? 's' : ''} added successfully` });
    } catch (error) {
      console.error('Error adding components:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add components';
      
      // On conflict, refresh and check
      if (errorMessage.includes('409') || errorMessage.includes('Conflict')) {
        const freshSession = await fetchSession(true);
        if (freshSession) {
          // Check which components were actually added
          const freshComponents = freshSession.components || [];
          const actuallyAdded = componentIds.filter(id => freshComponents.includes(id));
          
          if (actuallyAdded.length > 0) {
            setSession(freshSession);
            sessionRef.current = freshSession;
            showMessage({ type: 'success', text: `${actuallyAdded.length} component${actuallyAdded.length !== 1 ? 's' : ''} added successfully` });
            return;
          }
          
          // If no components were added, still update session state
          setSession(freshSession);
          sessionRef.current = freshSession;
        }
      }
      
      showMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleRemoveBugUrl = async (index: number) => {
    if (!session) return;
    if (isRemovingBugUrlRef.current) {
      return;
    }

    isRemovingBugUrlRef.current = true;

    try {
      // Get the bug to remove from current session state
      const bugUrls = session.bugUrls || [];
      if (index >= bugUrls.length) {
        // Already removed or invalid index
        return;
      }

      const bugToRemove = bugUrls[index];
      if (!bugToRemove || typeof bugToRemove === 'string') {
        // Legacy format or invalid - skip
        return;
      }

      const urlToRemove = bugToRemove.url;
      const componentId = bugToRemove.componentId;

      // Build updated bug URLs (remove the one we're deleting)
      const updatedBugUrls = bugUrls.filter((bug, i) => {
        if (i === index) return false;
        if (typeof bug === 'string') return true;
        // Also filter by URL and component to handle duplicates
        return !(bug.url.toLowerCase() === urlToRemove.toLowerCase() && 
                 bug.componentId === componentId);
      });

      // Update component statuses if needed
      const componentStatuses = [...(session.componentStatuses || [])];
      if (componentId) {
        // Check if there are any remaining bugs for this component
        const remainingBugsForComponent = updatedBugUrls.filter(bug => {
          if (typeof bug === 'string') return false;
          return bug.componentId === componentId;
        });

        const statusIndex = componentStatuses.findIndex(s => s.componentId === componentId);
        if (statusIndex >= 0) {
          if (remainingBugsForComponent.length === 0) {
            // No bugs remain - unmark as bugged
            componentStatuses[statusIndex] = {
              ...componentStatuses[statusIndex],
              bugged: false,
              buggedPlatforms: []
            };
          } else {
            // Update buggedPlatforms to only include platforms that still have bugs
            const platformsWithBugs = new Set<Platform>();
            remainingBugsForComponent.forEach(bug => {
              if (bug.platforms) {
                bug.platforms.forEach(platform => platformsWithBugs.add(platform));
              }
            });
            componentStatuses[statusIndex] = {
              ...componentStatuses[statusIndex],
              bugged: platformsWithBugs.size > 0,
              buggedPlatforms: Array.from(platformsWithBugs)
            };
          }
        }
      }

      // Optimistic update
      const optimisticSession: Session = {
        ...session,
        bugUrls: updatedBugUrls,
        componentStatuses
      };

      setSession(optimisticSession);
      sessionRef.current = optimisticSession;

      // Save to server
      try {
        const savedSession = await apiRequest<Session>(`/api/sessions/${session.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            bugUrls: updatedBugUrls,
            componentStatuses,
            version: session.version || 1,
            duration: timerRef.current,
            notes: notesRef.current || session.notes || null
          })
        }, { useAuth: true });
        
        // Use server response as source of truth
        setSession(savedSession);
        sessionRef.current = savedSession;
      } catch (error) {
        console.error('Error auto-saving session:', error);
        // Check if it's a 409 conflict
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isConflict = errorMessage.includes('409') || 
                          errorMessage.includes('Conflict') ||
                          errorMessage.toLowerCase().includes('conflict');
        
        if (isConflict) {
          // On conflict, refresh and retry once
          const freshSession = await fetchSession(true);
          if (freshSession) {
            const urlToRemoveStr = urlToRemove;
            
            // Check if bug was already removed
            const freshBugUrls = freshSession.bugUrls || [];
            const stillExists = freshBugUrls.some(bug => {
              if (typeof bug === 'string') return bug === urlToRemoveStr;
              return bug.url.toLowerCase() === urlToRemoveStr.toLowerCase() && 
                     bug.componentId === componentId;
            });

            if (!stillExists) {
              // Already removed - success
              setSession(freshSession);
              sessionRef.current = freshSession;
              return;
            }

            // Retry with fresh session
            const retryBugUrls = freshBugUrls.filter(bug => {
              if (typeof bug === 'string') return bug !== urlToRemoveStr;
              return !(bug.url.toLowerCase() === urlToRemoveStr.toLowerCase() && 
                       bug.componentId === componentId);
            });

            // Recalculate component statuses
            const retryComponentStatuses = [...(freshSession.componentStatuses || [])];
            if (componentId) {
              const remainingBugs = retryBugUrls.filter(bug => {
                if (typeof bug === 'string') return false;
                return bug.componentId === componentId;
              });

              const statusIdx = retryComponentStatuses.findIndex(s => s.componentId === componentId);
              if (statusIdx >= 0) {
                if (remainingBugs.length === 0) {
                  retryComponentStatuses[statusIdx] = {
                    ...retryComponentStatuses[statusIdx],
                    bugged: false,
                    buggedPlatforms: []
                  };
                } else {
                  const platformsWithBugs = new Set<Platform>();
                  remainingBugs.forEach(bug => {
                    if (bug.platforms) {
                      bug.platforms.forEach(platform => platformsWithBugs.add(platform));
                    }
                  });
                  retryComponentStatuses[statusIdx] = {
                    ...retryComponentStatuses[statusIdx],
                    bugged: platformsWithBugs.size > 0,
                    buggedPlatforms: Array.from(platformsWithBugs)
                  };
                }
              }
            }

            const retrySession = await apiRequest<Session>(`/api/sessions/${freshSession.id}`, {
              method: 'PUT',
              body: JSON.stringify({
                bugUrls: retryBugUrls,
                componentStatuses: retryComponentStatuses,
                version: freshSession.version || 1,
                duration: timerRef.current,
                notes: notesRef.current || freshSession.notes || null
              })
            }, { useAuth: true });

            setSession(retrySession);
            sessionRef.current = retrySession;
            return;
          }
        }
        
        // For other errors, keep optimistic update but show error
        showMessage({ 
          type: 'error', 
          text: `Failed to remove bug: ${errorMessage}. The bug has been removed locally.` 
        });
      }
    } finally {
      isRemovingBugUrlRef.current = false;
    }
  };

  const validateBeforeSubmit = (): boolean => {
    const newErrors: string[] = [];
    
    if (!session) return false;

    // Bug URLs are now optional - users can mark bugs without URLs
    // They can add URLs via the Additional Bug URLs section or the sub-dep bug URL input
    // No validation required for bug URLs

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) {
      return;
    }

    if (!session) return;

    // Stop timer before submitting
    setIsRunning(false);

    try {
      // Build the update payload explicitly, ensuring status is set
      const updatePayload: any = {
        status: 'completed',
        completedAt: new Date().toISOString(),
        duration: timer,
        unlock: true, // Unlock the session when completing
        activeUser: null,
        lockedAt: null,
        user: session.user // Include user to ensure permission check passes
      };
      
      // Include other fields that might have changed
      if (session.subDepStatuses) {
        updatePayload.subDepStatuses = session.subDepStatuses;
      }
      if (session.bugUrls) {
        updatePayload.bugUrls = session.bugUrls;
      }
      if (notes) {
        updatePayload.notes = notes;
      }

      const data = await apiRequest<Session>(`/api/sessions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updatePayload)
      }, { useAuth: true, setMessage });
      showTempMessage(setMessage, { type: 'success', text: 'Session submitted successfully' }, 3000, true);

      // Verify the status was actually saved
      if (data.status !== 'completed') {
        console.error('Status was not saved as completed. Response:', data);
        showMessage({ type: 'error', text: `ERROR: Session status was not saved as completed. Status is: ${data.status}. Please check the server logs.` });
        return; // Don't navigate if status wasn't saved
      }

      // CRITICAL: Update the session ref to 'completed' to prevent cleanup from overwriting it
      if (sessionRef.current) {
        sessionRef.current = { ...sessionRef.current, ...data, status: 'completed' as const };
      }

      // Navigate to workbook with completed tab active
      // Use replace: false to ensure state is passed correctly
      navigate('/', { 
        state: { selectedSessionId: data.id, activeTab: 'completed' },
        replace: false
      });
    } catch (error) {
      console.error('Error submitting session:', error);
      showMessage({ type: 'error', text: `Failed to submit session: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!session) {
    return <div className="form-container">Loading session...</div>;
  }

  const componentStatuses = session.componentStatuses || [];
  // Count bugs: only count linked bugs (bugUrls) to avoid double counting
  // Bugs are linked to components, and marking them as bugged is automatic
  const bugCount = session.bugUrls?.length || 0;

  return (
    <div className="form-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Testing Session</h1>
      </div>
      
      <div className="session-runner-layout">
        {/* Left Sidebar - Session Info & Controls */}
        <div className="session-runner-sidebar">
          <div className="card">
            <div className="session-id">{session.id}</div>
            <div className="card-title">{session.sessionType}</div>
            <div className="session-meta">
              <div><strong>User:</strong> {session.user}</div>
              <div><strong>Platforms:</strong> {session.platforms && session.platforms.length > 0 ? session.platforms.join(', ') : (session.platform || 'N/A')}</div>
              {session.milestone && (() => {
                const milestoneMeta = milestones[session.milestone];
                const dateInfo = milestoneMeta 
                  ? (milestoneMeta.endDate 
                      ? ` (${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')} - ${formatDate(milestoneMeta.endDate, 'MMM dd, yyyy')})`
                      : ` (${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')})`)
                  : '';
                return <div><strong>Milestone:</strong> {session.milestone}{dateInfo}</div>;
              })()}
              {session.startedAt && (
                <div><strong>Started:</strong> {formatDate(session.startedAt, 'MMM dd, yyyy h:mm a')}</div>
              )}
            </div>
            
            {session.testPlanUrl && (() => {
              // Extract test plan ID from URL (e.g., "http://localhost:3000/testplan/123" -> "123")
              const testPlanIdMatch = session.testPlanUrl.match(/\/testplan\/([^/?]+)/);
              const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
              
              
              const handleOpenTestPlan = async () => {
                if (!testPlanId) return;
                
                try {
                  // Fetch test plan and related data
                  const [testPlanData, featuresData, componentsData] = await Promise.all([
                    apiRequest<TestPlan>(`/api/testplans/${testPlanId}`, {}, { useAuth: true }),
                    apiRequest<Feature[]>('/api/features', {}, { useAuth: true }),
                    apiRequest<Component[]>('/api/components', {}, { useAuth: true })
                  ]);
                  
                  setSelectedTestPlan(testPlanData);
                  setTestPlanFeatures(Array.isArray(featuresData) ? featuresData : []);
                  setTestPlanComponents(Array.isArray(componentsData) ? componentsData : []);
                } catch (error) {
                  console.error('Error fetching test plan:', error);
                  showTempMessage(setMessage, { type: 'error', text: 'Failed to load test plan' });
                }
              };
              
              return (
                <div className="session-info-section">
                  <strong>Test Plan:</strong>{' '}
                  {testPlanId ? (
                    <button
                      type="button"
                      onClick={handleOpenTestPlan}
                      style={{ 
                        color: 'var(--cyan-glow)', 
                        textDecoration: 'none',
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {testPlanName || session.testPlanUrl}
                    </button>
                  ) : (
                    <a href={session.testPlanUrl} target="_blank" rel="noopener noreferrer">
                      {session.testPlanUrl}
                    </a>
                  )}
                </div>
              );
            })()}
            {session.referenceUrl && (
              <div className="session-info-section" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}>
                <strong style={{ flexShrink: 0 }}>Reference:</strong>
                <a 
                  href={session.referenceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flex: 1,
                  }}
                  title={session.referenceUrl}
                >
                  {session.referenceUrl}
                </a>
              </div>
            )}
            
            {session.deadline && (
              <div className="session-info-section">
                <strong>Deadline:</strong> {formatDate(session.deadline, 'MMM dd, yyyy')}
              </div>
            )}
            <span className={`status-badge status-${session.status}`}>
              {session.status.toUpperCase()}
            </span>
          </div>

          <div className="card timer-card">
            <div className="timer-label">Session Duration</div>
            <div className="timer-display">{formatTime(timer)}</div>
        {!isRunning && session.status === 'inprogress' && (
          <button
            className={`btn timer-button ${timer === 0 ? 'btn-success' : 'btn-primary'}`}
            onClick={async () => {
              setIsRunning(true);
              // Update startedAt to account for elapsed time
              if (session) {
                const now = new Date();
                const adjustedStart = new Date(now.getTime() - timer * 1000);
                try {
                  const updateData: any = {
                    startedAt: adjustedStart.toISOString()
                  };
                  // Set firstStartedAt only if it doesn't exist (first time starting timer)
                  if (!session.firstStartedAt) {
                    updateData.firstStartedAt = adjustedStart.toISOString();
                  }
                  const updatedSession = await apiRequest<Session>(`/api/sessions/${session.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(updateData)
                  }, { useAuth: true });
                  setSession(updatedSession);
                } catch (error) {
                  console.error('Error updating session:', error);
                }
              }
            }}
          >
            {timer === 0 ? 'Start Timer' : 'Resume Timer'}
          </button>
        )}
            {isRunning && (
              <button
                className="btn btn-secondary timer-button"
                onClick={async () => {
                  setIsRunning(false);
                  // Save current duration and clear startedAt to indicate timer is paused
                  // This ensures on refresh we use saved duration instead of calculating from startedAt
                  if (session) {
                    try {
                      const updatedSession = await apiRequest<Session>(`/api/sessions/${session.id}`, {
                        method: 'PUT',
                        body: JSON.stringify({
                          duration: timer,
                          startedAt: null, // Clear startedAt when pausing
                          subDepStatuses: session.subDepStatuses,
                          bugUrls: session.bugUrls,
                          notes: notes || undefined
                        })
                      }, { useAuth: true });
                      setSession(updatedSession); // Update local session state
                    } catch (error) {
                      console.error('Error saving duration:', error);
                    }
                  }
                }}
              >
                Pause Timer
              </button>
            )}
          </div>

          <div className="card">
            <h2 style={{ marginBottom: '1rem' }}>Actions</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddComponentModal(true)}
                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', width: '100%' }}
              >
                Add Components
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setAddBugModalData(null);
                  setShowAddBugModal(true);
                }}
                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', width: '100%' }}
              >
                Add Bug
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setSelectedComponentForTestCase(null);
                  setShowCreateTestCaseModal(true);
                }}
                disabled={components.length === 0}
                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', width: '100%' }}
              >
                Create Test Case
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setPrFeedbackModalData(null);
                  setShowPRFeedbackModal(true);
                }}
                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', width: '100%' }}
              >
                Add PR Feedback
              </button>
            </div>
          </div>

          <div className="card">
            <h2>Notes</h2>
            <textarea
              value={notes}
              onChange={async (e) => {
                const newNotes = e.target.value;
                setNotes(newNotes);
                
                // Auto-save notes
                if (session) {
                  try {
                    await apiRequest(`/api/sessions/${session.id}`, {
                      method: 'PUT',
                      body: JSON.stringify({
                        notes: newNotes
                      })
                    }, { useAuth: true });
                  } catch (error) {
                    console.error('Error auto-saving notes:', error);
                  }
                }
              }}
              placeholder="Add notes about this testing session..."
              className="notes-textarea-compact"
            />
          </div>

          <div className="card">
            <h2 style={{ margin: 0, marginBottom: '1rem' }}>Session Summary</h2>
            <div className="session-summary-stats">
              <div className="summary-stat">
                <div className="summary-stat-value">{bugCount}</div>
                <div className="summary-stat-label">Bugs Found</div>
              </div>
              <div className="summary-stat">
                <div className="summary-stat-value">{componentStatuses.filter(s => s.tested).length} / {components.length}</div>
                <div className="summary-stat-label">Tested</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Main Content - Component Cards */}
        <div className="session-runner-main">
          <div className="card">
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h2 style={{ margin: 0 }}>Components</h2>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    data-filter-button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--surface)',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--primary)';
                      e.currentTarget.style.backgroundColor = 'var(--primary-transparent-light)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                      e.currentTarget.style.backgroundColor = 'var(--surface)';
                    }}
                  >
                    <span>Add filter</span>
                    <span style={{ fontSize: '0.75rem' }}>▼</span>
                  </button>
                  
                  {/* Filter Dropdown */}
                  {showFilterDropdown && session && (
                    <div
                      data-filter-dropdown
                      className="card"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '0.5rem',
                        minWidth: '240px',
                        maxWidth: '280px',
                        zIndex: 1000,
                        padding: 0,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Status Filter Item */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setExpandedFilterSection(expandedFilterSection === 'status' ? null : 'status')}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.75rem',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderBottom: '1px solid var(--border-color)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: 'var(--text-primary)',
                            textAlign: 'left',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--surface-alt)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span style={{ textAlign: 'left' }}>Status</span>
                          <span style={{ 
                            fontSize: '0.75rem',
                            transform: expandedFilterSection === 'status' ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            marginLeft: 'auto'
                          }}>
                            ▼
                          </span>
                        </button>
                        {expandedFilterSection === 'status' && (
              <div style={{ 
                            padding: 0,
                            borderBottom: '1px solid var(--border-color)',
                            backgroundColor: 'var(--surface-alt)'
                          }}>
                            <label style={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer', 
                              fontSize: '0.875rem',
                              color: 'var(--text-primary)'
                            }}>
                              <input
                                type="radio"
                                name="status-filter"
                                checked={componentFilterStatus === 'tested'}
                                onChange={() => setComponentFilterStatus('tested')}
                                style={{ 
                                  cursor: 'pointer',
                                  accentColor: 'var(--primary)',
                                  margin: 0,
                                  marginRight: '0.75rem',
                                  padding: 0
                                }}
                              />
                              <span>Tested</span>
                            </label>
                            <label style={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer', 
                              fontSize: '0.875rem',
                              color: 'var(--text-primary)'
                            }}>
                              <input
                                type="radio"
                                name="status-filter"
                                checked={componentFilterStatus === 'untested'}
                                onChange={() => setComponentFilterStatus('untested')}
                                style={{ 
                                  cursor: 'pointer',
                                  accentColor: 'var(--primary)',
                                  margin: 0,
                                  marginRight: '0.75rem',
                                  padding: 0
                                }}
                              />
                              <span>Untested</span>
                            </label>
                          </div>
                        )}
                      </div>
                      
                      {/* Platforms Filter Item */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setExpandedFilterSection(expandedFilterSection === 'platforms' ? null : 'platforms')}
                          style={{
                            width: '100%',
                display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.75rem',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderBottom: expandedFilterSection === 'platforms' ? 'none' : '1px solid var(--border-color)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: 'var(--text-primary)',
                            textAlign: 'left',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--surface-alt)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span style={{ textAlign: 'left' }}>Platforms</span>
                          <span style={{ 
                            fontSize: '0.75rem',
                            transform: expandedFilterSection === 'platforms' ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            marginLeft: 'auto'
                          }}>
                            ▼
                          </span>
                        </button>
                        {expandedFilterSection === 'platforms' && (
                          <div style={{ 
                            padding: 0,
                            borderBottom: '1px solid var(--border-color)',
                            backgroundColor: 'var(--surface-alt)',
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}>
                            {(session.platforms || (session.platform ? [session.platform] : ['Windows'])).map(platform => (
                              <label 
                                key={platform} 
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0.5rem 0.75rem',
                                  cursor: 'pointer', 
                                  fontSize: '0.875rem',
                                  color: 'var(--text-primary)'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={componentFilterPlatforms.includes(platform)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setComponentFilterPlatforms([...componentFilterPlatforms, platform]);
                                    } else {
                                      setComponentFilterPlatforms(componentFilterPlatforms.filter(p => p !== platform));
                                    }
                                  }}
                                  style={{ 
                                    cursor: 'pointer',
                                    accentColor: 'var(--primary)',
                                    margin: 0,
                                    marginRight: '0.75rem',
                                    padding: 0
                                  }}
                                />
                                <span>{platform}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Clear Selection */}
                      {(componentFilterStatus !== null || componentFilterPlatforms.length > 0) && (
                        <div>
                          <button
                            type="button"
                            onClick={() => {
                              setComponentFilterStatus(null);
                              setComponentFilterPlatforms([]);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              fontSize: '0.875rem',
                              color: 'var(--primary)',
                              backgroundColor: 'transparent',
                              border: 'none',
                              borderTop: '1px solid var(--border-color)',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--surface-alt)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Clear selection
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Active Filter Pills */}
              {(componentFilterStatus !== null || componentFilterPlatforms.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                  {componentFilterStatus !== null && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.375rem 0.75rem',
                        backgroundColor: 'var(--primary-transparent-light)',
                        color: 'var(--primary)',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary)'
                      }}
                    >
                      Status: {componentFilterStatus === 'tested' ? 'Tested' : 'Untested'}
                    <button
                        type="button"
                        onClick={() => setComponentFilterStatus(null)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--primary)',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '1rem',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {componentFilterPlatforms.map(platform => (
                    <span
                      key={platform}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.375rem 0.75rem',
                        backgroundColor: 'var(--primary-transparent-light)',
                        color: 'var(--primary)',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary)'
                      }}
                    >
                      Platform: {platform}
                      <button
                        type="button"
                        onClick={() => setComponentFilterPlatforms(componentFilterPlatforms.filter(p => p !== platform))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--primary)',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '1rem',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {(componentFilterStatus !== null || componentFilterPlatforms.length > 0) && (
                    <button
                      type="button"
                      onClick={() => {
                        setComponentFilterStatus(null);
                        setComponentFilterPlatforms([]);
                      }}
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        color: 'var(--text-secondary)',
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Clear all
                    </button>
                  )}
              </div>
            )}
            </div>

          {components.length === 0 ? (
            <p>No components selected for this session.</p>
          ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {components
                  .filter(component => {
                    // No filters = show all
                    if (componentFilterStatus === null && componentFilterPlatforms.length === 0) return true;
                    
                    const status = componentStatuses.find(s => s.componentId === component.id);
                    const testedPlatforms = status?.testedPlatforms || [];
                    const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : ['Windows']);
                    
                    // Determine which platforms to check
                    const platformsToCheck = componentFilterPlatforms.length > 0 
                      ? componentFilterPlatforms 
                      : sessionPlatforms;
                    
                    // If status filter is set, check component status on the selected platforms
                    if (componentFilterStatus !== null) {
                      if (componentFilterStatus === 'tested') {
                        // Component must be tested on at least one of the selected platforms
                        return platformsToCheck.some(platform => testedPlatforms.includes(platform));
                      } else if (componentFilterStatus === 'untested') {
                        // Component must not be tested on any of the selected platforms
                        return !platformsToCheck.some(platform => testedPlatforms.includes(platform));
                      }
                    }
                    
                    // If only platforms are selected (no status), show all components
                    return true;
                  })
                  .map(component => {
                      const status = componentStatuses.find(s => s.componentId === component.id) || {
                        componentId: component.id,
                        tested: false,
                        testedPlatforms: [],
                        bugged: false,
                        buggedPlatforms: []
                      };
                      
                      const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : ['Windows']);
                    const testedPlatforms = status.testedPlatforms || [];
                    
                    // Count bugs and PR feedback for this component
                    const linkedBugs = (session.bugUrls || []).filter(bugUrl => {
                      if (typeof bugUrl === 'string') return false;
                      return (bugUrl.componentId || bugUrl.subDepId) === component.id;
                    });
                    const componentFeedbacks = (session.prFeedbacks || []).filter(feedback => 
                      feedback.componentId === component.id
                    );
                      const componentTestCases = testCases.filter(tc => tc.componentId === component.id);
                      
                      return (
                    <div
                      key={component.id}
                      style={{
                        padding: '1.25rem',
                        backgroundColor: 'var(--surface-alt)',
                        border: '3px solid var(--grid-line)',
                        borderRadius: '8px',
                        position: 'relative',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
                        marginBottom: '1.5rem'
                      }}
                    >
                      {/* Component Name */}
                      <div style={{ 
                        fontWeight: '600', 
                        marginBottom: '1rem',
                        fontSize: '1rem',
                        color: 'var(--text-primary)',
                        paddingBottom: '0.75rem',
                        borderBottom: '1px solid var(--grid-line)'
                      }}>
                              {component.name}
                            </div>
                      
                      {/* Platforms Row - Primary Action */}
                      <div style={{ 
                        marginBottom: '1.25rem'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: 'var(--text-secondary)',
                          marginBottom: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Mark as Tested
                            </div>
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '0.75rem',
                          alignItems: 'center'
                        }}>
                          {sessionPlatforms.map(platform => {
                            const isTested = testedPlatforms.includes(platform);
                            return (
                                <button
                                key={platform}
                                type="button"
                                onClick={() => handlePlatformTestedToggle(component.id, platform)}
                                style={{
                                  padding: '0.75rem 1.25rem',
                                  fontSize: '0.9375rem',
                                  border: `2px solid ${isTested ? 'var(--primary)' : 'var(--grid-line)'}`,
                                  borderRadius: '6px',
                                  backgroundColor: isTested ? 'var(--primary)' : 'var(--surface)',
                                  color: isTested ? 'white' : 'var(--text-primary)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.625rem',
                                  fontWeight: '600',
                                  transition: 'all 0.2s',
                                  boxShadow: isTested ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                                  minWidth: '100px',
                                  justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isTested) {
                                    e.currentTarget.style.borderColor = 'var(--primary)';
                                    e.currentTarget.style.backgroundColor = 'var(--primary-transparent-light)';
                                    e.currentTarget.style.color = 'var(--primary)';
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'var(--primary-bright)';
                                    e.currentTarget.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.15)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isTested) {
                                    e.currentTarget.style.borderColor = 'var(--grid-line)';
                                    e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
                                    e.currentTarget.style.color = 'var(--text-primary)';
                                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'var(--primary)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }
                                }}
                                title={isTested ? `Tested on ${platform} (click to unmark)` : `Mark as tested on ${platform}`}
                              >
                                {platform}
                                {isTested && <span style={{ fontSize: '1.1rem' }}>✓</span>}
                                </button>
                              );
                          })}
                        </div>
                          </div>
                          
                      {/* Test Cases - List */}
                          {componentTestCases.length > 0 && (
                        <div style={{ 
                          marginBottom: '0.75rem',
                          padding: '0.75rem',
                          backgroundColor: 'var(--surface)',
                          borderRadius: '4px',
                          border: '1px solid var(--grid-line)'
                        }}>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600', 
                            color: 'var(--text-secondary)',
                            marginBottom: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Test Cases ({componentTestCases.length})
                              </div>
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '0.5rem',
                            fontSize: '0.875rem'
                          }}>
                                {componentTestCases.map(testCase => {
                                  const tcDisplayId = testCase.testCaseNumber 
                                    ? (projectCode ? `${projectCode}-TC-${testCase.testCaseNumber}` : `TC-${testCase.testCaseNumber}`)
                                    : null;
                                  return (
                                <button
                                    key={testCase.id} 
                                  onClick={() => setViewingTestCase(testCase)}
                                    style={{ 
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: '1px solid var(--border-color)',
                                    padding: '0.625rem 0.5rem',
                                    color: 'var(--text-primary)',
                                    cursor: 'pointer',
                                    fontSize: 'inherit',
                                    textAlign: 'left',
                                      display: 'flex', 
                                      alignItems: 'center', 
                                    gap: '0.75rem',
                                    borderRadius: '0',
                                    transition: 'all 0.2s',
                                    fontWeight: '400'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.08)';
                                    e.currentTarget.style.color = 'var(--cyan-glow)';
                                    e.currentTarget.style.paddingLeft = '0.75rem';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'var(--text-primary)';
                                    e.currentTarget.style.paddingLeft = '0.5rem';
                                  }}
                                >
                                    {tcDisplayId && (
                                      <span style={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                      padding: '0.15rem 0.4rem',
                                        backgroundColor: 'var(--cyan-glow)',
                                        color: 'var(--surface)',
                                        borderRadius: '3px',
                                        fontFamily: 'monospace',
                                      border: 'none',
                                        flexShrink: 0
                                      }}>
                                        {tcDisplayId}
                                      </span>
                                    )}
                                  <span style={{ flex: 1, textAlign: 'left' }}>
                                        {testCase.title}
                                  </span>
                                      </button>
                              );
                            })}
                                    </div>
                        </div>
                      )}
                          
                      {/* Bottom Row - Count Pills and Action Buttons */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '0.75rem',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid var(--border-color)'
                      }}>
                        {/* Count Pills */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem',
                          alignItems: 'center'
                        }}>
                          {linkedBugs.length > 0 && (
                                      <span style={{ 
                              padding: '0.5rem 0.875rem',
                              backgroundColor: 'var(--danger)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <span>🐛</span>
                              <span>{linkedBugs.length} Bug{linkedBugs.length !== 1 ? 's' : ''}</span>
                                      </span>
                                    )}
                          {componentFeedbacks.length > 0 && (
                            <span style={{
                              padding: '0.5rem 0.875rem',
                              backgroundColor: 'var(--primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <span>💬</span>
                              <span>{componentFeedbacks.length} PR Feedback</span>
                            </span>
                          )}
                        </div>
                        
                        {/* Action Buttons - Bottom Right */}
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddBugModalData({
                                featureId: component.featureId,
                                componentId: component.id,
                                testedPlatforms: testedPlatforms
                              });
                              setShowAddBugModal(true);
                            }}
                            style={{ 
                              fontSize: '0.875rem', 
                              padding: '0.5rem 1rem',
                              backgroundColor: 'var(--primary)',
                              color: 'white',
                              border: 'none',
                              fontWeight: '500',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--primary-bright)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--primary)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            Add Bug
                                    </button>
                          <button
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPrFeedbackModalData({
                                featureId: component.featureId,
                                componentId: component.id
                              });
                              setShowPRFeedbackModal(true);
                            }}
                                        style={{ 
                                          fontSize: '0.875rem',
                              padding: '0.5rem 1rem',
                              backgroundColor: 'var(--primary)',
                              color: 'white',
                              border: 'none',
                              fontWeight: '500',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--primary-bright)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--primary)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            Add PR Feedback
                          </button>
                                        <button
                            className="btn btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingComponent(component);
                            }}
                            style={{ 
                              fontSize: '0.875rem', 
                              padding: '0.5rem 1rem',
                              backgroundColor: 'var(--surface)',
                              border: '2px solid var(--border-color)',
                              color: 'var(--text-primary)',
                              fontWeight: '500',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--cyan-glow)';
                              e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
                              e.currentTarget.style.color = 'var(--cyan-glow)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                              e.currentTarget.style.backgroundColor = 'var(--surface)';
                              e.currentTarget.style.color = 'var(--text-primary)';
                            }}
                          >
                            View Details
                                      </button>
                                      </div>
                              </div>
                        </div>
                      );
                    })}
                  </div>
          )}
          </div>
        </div>
      </div>

      <Toast message={message} />

      {errors.length > 0 && (
        <div className="card error-card session-runner-errors">
          <h3 className="error-title">Errors:</h3>
          <ul>
            {errors.map((error, index) => (
              <li key={index} className="error-text">{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Add Bug Modal */}
      {session && (
        <AddBugModal
          session={session}
          allFeatures={allFeatures}
          allComponents={allComponents}
          bugLinks={bugLinks}
          isOpen={showAddBugModal}
          onClose={() => {
            setShowAddBugModal(false);
            setAddBugModalData(null);
          }}
          onBugAdded={handleBugAdded}
          showMessage={showMessage}
          prefillData={addBugModalData || undefined}
          zIndex={viewingComponent ? 3000 : 1000}
        />
      )}

      {/* Add Component Modal */}
      {session && (
        <AddComponentModal
          session={session}
          allFeatures={allFeatures}
          allComponents={allComponents}
          allPlatforms={allPlatforms}
          isOpen={showAddComponentModal}
          onClose={() => setShowAddComponentModal(false)}
          onComponentsAdded={handleComponentsAdded}
          showMessage={showMessage}
        />
      )}

      {/* Add Test Case Modal */}
      {showCreateTestCaseModal && (
        <AddTestCaseModal
          allComponents={components}
          allFeatures={allFeatures}
          isOpen={showCreateTestCaseModal}
          onClose={() => {
            setShowCreateTestCaseModal(false);
            setSelectedComponentForTestCase(null);
            setEditingTestCase(null);
          }}
          onTestCaseAdded={handleTestCaseAdded}
          onTestCaseUpdated={handleTestCaseAdded}
          showMessage={showMessage}
          prefillData={selectedComponentForTestCase ? { componentId: selectedComponentForTestCase } : undefined}
          editingTestCase={editingTestCase}
        />
      )}

      {/* Test Case Review Modal */}
      {viewingTestCase && (
        <TestCaseReviewModal
          testCase={viewingTestCase}
          component={components.find(c => c.id === viewingTestCase.componentId)}
          feature={allFeatures.find(f => f.id === (viewingTestCase.featureId || components.find(c => c.id === viewingTestCase.componentId)?.featureId))}
          projectCode={projectCode}
          onClose={() => setViewingTestCase(null)}
          onEdit={(tc) => {
            setViewingTestCase(null);
            setEditingTestCase(tc);
            setShowCreateTestCaseModal(true);
          }}
          zIndex={viewingComponent ? 3000 : 2000}
        />
      )}

      {/* Test Plan Details Modal */}
      {selectedTestPlan && (
        <TestPlanDetails
          scope={selectedTestPlan}
          onClose={() => setSelectedTestPlan(null)}
          features={testPlanFeatures}
          components={testPlanComponents}
          projectCode={projectCode}
          onEdit={() => {
            // Don't allow editing from session runner - just close modal
            setSelectedTestPlan(null);
          }}
          onDelete={() => {
            // Don't allow deleting from session runner - just close modal
            setSelectedTestPlan(null);
          }}
          zIndex={2000}
        />
      )}

      {/* PR Feedback Modal */}
      {session && (
        <PRFeedbackModal
          session={session}
          allFeatures={allFeatures}
          allComponents={allComponents}
          isOpen={showPRFeedbackModal}
          onClose={() => {
            setShowPRFeedbackModal(false);
            setPrFeedbackModalData(null);
            setEditingPRFeedback(null);
          }}
          onFeedbackAdded={handlePRFeedbackAdded}
          showMessage={showMessage}
          prefillData={prFeedbackModalData || undefined}
          editingFeedback={editingPRFeedback}
        />
      )}

      {/* Component Detail Modal */}
      {session && viewingComponent && (
        <ComponentDetailModal
          isOpen={!!viewingComponent}
          onClose={() => setViewingComponent(null)}
          component={viewingComponent}
          session={session}
          testCases={testCases}
          features={allFeatures}
          projectCode={projectCode}
          onPlatformTestedToggle={handlePlatformTestedToggle}
          onBugAdded={handleBugAdded}
          onPRFeedbackAdded={handlePRFeedbackAdded}
          onRemoveBug={handleRemoveBugUrl}
          onRemovePRFeedback={handleRemovePRFeedback}
          onEditPRFeedback={(feedback) => {
            setEditingPRFeedback(feedback);
            setPrFeedbackModalData({
              featureId: feedback.featureId,
              componentId: feedback.componentId
            });
            setViewingComponent(null);
            setShowPRFeedbackModal(true);
          }}
          onViewTestCase={(testCase, zIndex) => {
            setViewingTestCase(testCase);
            // zIndex is handled by TestCaseReviewModal component
          }}
          onAddBugClick={() => {
            if (!viewingComponent) return;
            const status = componentStatuses.find(s => s.componentId === viewingComponent.id) || {
              componentId: viewingComponent.id,
              tested: false,
              testedPlatforms: [],
              bugged: false,
              buggedPlatforms: []
            };
            setAddBugModalData({
              featureId: viewingComponent.featureId,
              componentId: viewingComponent.id,
              testedPlatforms: status.testedPlatforms || []
            });
            setShowAddBugModal(true);
          }}
          onAddPRFeedbackClick={() => {
            if (!viewingComponent) return;
            setPrFeedbackModalData({
              featureId: viewingComponent.featureId,
              componentId: viewingComponent.id
            });
            setViewingComponent(null);
            setShowPRFeedbackModal(true);
          }}
          showMessage={showMessage}
        />
      )}

      {/* Hide floating buttons when any modal is open */}
      {!showAddBugModal && !showAddComponentModal && !showCreateTestCaseModal && !selectedTestPlan && !viewingTestCase && (
        <div className="session-runner-actions-floating">
          {components.length > 0 && (
            <button 
              className="btn btn-secondary floating-action-btn" 
              onClick={handleMarkAllAsTested}
              title="Mark all untested components as tested on all selected platforms"
            >
              Mark All as Tested
            </button>
          )}
          <button 
            className="btn btn-primary floating-action-btn" 
            onClick={async () => {
            if (!session) return;
            
            // Stop timer before saving
            setIsRunning(false);
            
            try {
              await apiRequest(`/api/sessions/${session.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                  status: 'inprogress',
                  subDepStatuses: session.subDepStatuses,
                  bugUrls: session.bugUrls,
                  duration: timer,
                  notes: notes || undefined,
                  unlock: true // Unlock the session when saving and exiting
                })
              }, { useAuth: true, setMessage });
              showTempMessage(setMessage, { type: 'success', text: 'Session saved successfully' }, 3000, true);
              navigate('/sessions');
            } catch (error) {
              console.error('Error saving session:', error);
              // Check if it's a 423 locked error
              if (error instanceof Error && error.message.includes('423')) {
                showMessage({ type: 'error', text: `Cannot save: ${error.message}` });
                return;
              }
              showMessage({ type: 'error', text: 'Failed to save session' });
            }
          }}
        >
          Save and Exit
        </button>
          <button className="btn btn-success floating-action-btn" onClick={handleSubmit}>
            Submit Session
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionRunner;

